/**
 * routes.tsx — central route table for the iTow web app.
 *
 * Every feature module is lazy-imported at the route level to keep the
 * initial bundle small (see SPEC.md §Performance).
 *
 * Placeholder routes are direct imports (tiny); real feature routes will be
 * converted to React.lazy() as their feature slices are built.
 */
import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';
import AppShell from './shared/AppShell';
import { usePageTitle } from './shared';
import HomePlaceholder from './pages/HomePlaceholder';
import HomeRouter from './pages/home/HomeRouter';
import { RequireAuth } from './core/rbac';
import { ErrorBoundary } from './shared/ErrorBoundary';
import { TESTING_CONSOLE_ENABLED } from './config/flags';

// Lazy: catalog routes split in slice 152; tow-requests, claims, routings in slice 153; documents, lookups, recycle-bin, configuration in slice 154; auth-flow components in slice 185.
const Login = lazy(() =>
  import('./pages/login').then((m) => ({ default: m.Login })),
);
const ForgotPassword = lazy(() =>
  import('./pages/forgot-password').then((m) => ({ default: m.ForgotPassword })),
);

// Stub catalog pages — preview-only, render the endpoint as a basic table
// until dedicated CRUD pages are built.
const IssuesPage = lazy(() => import('./pages/issues/IssuesPage'));
const WorkflowsPage = lazy(() => import('./pages/workflows/WorkflowsPage'));
const UpdatesPage = lazy(() => import('./pages/updates/UpdatesPage'));
const UpdatesArchive = lazy(() => import('./pages/updates/UpdatesArchive'));
const AdministratorsCatalog = lazy(() => import('./pages/configuration/AdministratorsCatalog'));
const ApplicationsPage = lazy(() => import('./pages/configuration/ApplicationsPage'));
const ActivityPage = lazy(() => import('./pages/configuration/ActivityPage'));
const TelematicsDevicesPage = lazy(() => import('./pages/telematics/TelematicsDevicesPage'));
const CatalogStubPage = lazy(() =>
  import('./pages/catalog-stub/CatalogStubPage'),
);

const CustomersList = lazy(() =>
  import('./pages/customers').then((m) => ({ default: m.CustomersList })),
);
const CustomerDetail = lazy(() =>
  import('./pages/customers').then((m) => ({ default: m.CustomerDetail })),
);
const TowProvidersList = lazy(() =>
  import('./pages/tow-providers').then((m) => ({ default: m.TowProvidersList })),
);
const TowProviderDetail = lazy(() =>
  import('./pages/tow-providers').then((m) => ({ default: m.TowProviderDetail })),
);
const DriversList = lazy(() =>
  import('./pages/drivers').then((m) => ({ default: m.DriversList })),
);
const DriverDetail = lazy(() =>
  import('./pages/drivers').then((m) => ({ default: m.DriverDetail })),
);
const UnitsList = lazy(() =>
  import('./pages/units').then((m) => ({ default: m.UnitsList })),
);
const TrailersList = lazy(() =>
  import('./pages/trailers').then((m) => ({ default: m.TrailersList })),
);
const UnitDetail = lazy(() =>
  import('./pages/units').then((m) => ({ default: m.UnitDetail })),
);
const AdjustersList = lazy(() =>
  import('./pages/adjusters').then((m) => ({ default: m.AdjustersList })),
);
const AdjusterDetail = lazy(() =>
  import('./pages/adjusters').then((m) => ({ default: m.AdjusterDetail })),
);

// Lazy: tow-requests, claims, routings (slice 153)
const TowRequestsList = lazy(() =>
  import('./pages/tow-requests').then((m) => ({ default: m.TowingRequestsList })),
);
const RoadsideAssistanceList = lazy(() =>
  import('./pages/tow-requests').then((m) => ({ default: m.RoadsideAssistanceList })),
);
// One route, two screens: a tow is a claim with an auction attached, a roadside
// job is a service call, and `?serviceType` says which is being opened.
const TowRequestDetail = lazy(() =>
  import('./pages/tow-requests').then((m) => ({ default: m.RequestDetailRouter })),
);
const OnboardingPage = lazy(() =>
  import('./pages/onboarding').then((m) => ({ default: m.OnboardingPage })),
);
const CustomerProfilePage = lazy(() =>
  import('./pages/customer-profile').then((m) => ({ default: m.CustomerProfilePage })),
);
const SignupPage = lazy(() =>
  import('./pages/signup').then((m) => ({ default: m.SignupPage })),
);
const ChatPage = lazy(() =>
  import('./pages/chat').then((m) => ({ default: m.ChatPage })),
);
const DocumentRequestsList = lazy(() =>
  import('./pages/document-requests').then((m) => ({ default: m.DocumentRequestsList })),
);
const ProviderProfilePage = lazy(() =>
  import('./pages/provider-profile').then((m) => ({ default: m.ProviderProfilePage })),
);
const ProviderPricingOverview = lazy(() =>
  import('./pages/provider-pricing').then((m) => ({ default: m.ProviderPricingOverview })),
);
const ProviderEquipmentPage = lazy(() =>
  import('./pages/provider-equipment').then((m) => ({ default: m.ProviderEquipmentPage })),
);
const EquipmentCoveragePage = lazy(() =>
  import('./pages/provider-equipment').then((m) => ({ default: m.EquipmentCoveragePage })),
);
const ProviderMapPage = lazy(() =>
  import('./pages/provider-map').then((m) => ({ default: m.ProviderMapPage })),
);
const ProviderRankingPage = lazy(() =>
  import('./pages/provider-ranking').then((m) => ({ default: m.ProviderRankingPage })),
);
const DispatchSettingsPage = lazy(() =>
  import('./pages/dispatch-settings').then((m) => ({ default: m.DispatchSettingsPage })),
);
const AdminReportsPage = lazy(() =>
  import('./pages/reports').then((m) => ({ default: m.AdminReportsPage })),
);
const ClaimsList = lazy(() =>
  import('./pages/claims').then((m) => ({ default: m.ClaimsList })),
);
const ClaimDetail = lazy(() =>
  import('./pages/claims').then((m) => ({ default: m.ClaimDetail })),
);
const RoutingsList = lazy(() =>
  import('./pages/routings').then((m) => ({ default: m.RoutingsList })),
);
const RoutingDetail = lazy(() =>
  import('./pages/routings').then((m) => ({ default: m.RoutingDetail })),
);

// Lazy: the QA / Testing Console. Lazy matters more here than elsewhere — the
// chunk carries the whole test plan and firebase/storage, and no customer build
// should download either.
const TestingConsolePage = lazy(() =>
  import('./pages/testing').then((m) => ({ default: m.TestingConsolePage })),
);

// Lazy: preferences (slice 320)
const PreferencesPage = lazy(() =>
  import('./pages/preferences').then((m) => ({ default: m.PreferencesPage })),
);

// Lazy: documents, lookups, recycle-bin, configuration (slice 154)
const DocumentsList = lazy(() =>
  import('./pages/documents').then((m) => ({ default: m.DocumentsList })),
);
const LookupsList = lazy(() =>
  import('./pages/lookups').then((m) => ({ default: m.LookupsList })),
);
const RecycleBinList = lazy(() =>
  import('./pages/recycle-bin').then((m) => ({ default: m.RecycleBinList })),
);
const RolesList = lazy(() =>
  import('./pages/configuration').then((m) => ({ default: m.RolesList })),
);
const UsersList = lazy(() =>
  import('./pages/configuration').then((m) => ({ default: m.UsersList })),
);
const TermsConditionsList = lazy(() =>
  import('./pages/configuration').then((m) => ({ default: m.TermsConditionsList })),
);
const RolePermissionMatrix = lazy(() =>
  import('./pages/configuration').then((m) => ({ default: m.RolePermissionMatrix })),
);

function RouteFallback() {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '40vh',
      }}
      role="status"
      aria-busy="true"
      aria-live="polite"
    >
      <CircularProgress aria-label={t('home.loading')} />
    </Box>
  );
}

function NotFound() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  usePageTitle('errors.notFound');
  return (
    <AppShell>
      <Box sx={{ p: '2rem', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 4 }}>
        <Typography variant="h1" component="h1" sx={{ fontSize: '8rem', fontWeight: 'bold', color: 'primary.main', lineHeight: 1 }}>
          404
        </Typography>
        <Typography variant="h5" component="h2">
          {t('errors.notFound')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500 }}>
          {t('errors.notFoundDescription')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Button variant="outlined" onClick={() => navigate(-1)} startIcon={<ArrowBackIcon />}>
            {t('errors.goBack')}
          </Button>
          <Button variant="contained" onClick={() => navigate('/')} startIcon={<HomeIcon />}>
            {t('errors.goHome')}
          </Button>
        </Box>
      </Box>
    </AppShell>
  );
}

export default function AppRoutes() {
  return (
    <ErrorBoundary>
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      <Route
        path="/"
        element={
          <RequireAuth>
            <AppShell>
              <HomeRouter />
            </AppShell>
          </RequireAuth>
        }
      />

      {/* Backend hardcodes the Home menu route as "/home" (LoginBLL.GetMenuV2);
          redirect that to "/" so the menu link resolves to HomeRouter. */}
      <Route path="/home" element={<Navigate to="/" replace />} />

      {/* Tow-requests — nested routes so list and detail share one AppShell render.
          Permission gate is a follow-up slice; auth-only for now. */}
      <Route
        path="/tow-requests"
        element={
          <RequireAuth>
            <AppShell>
              <Outlet />
            </AppShell>
          </RequireAuth>
        }
      >
        <Route index element={<TowRequestsList />} />
        <Route path=":id" element={<TowRequestDetail />} />
      </Route>

      {/* Roadside assistance — the same board pinned to the other service type.
          Separate page rather than a dropdown on the towing list: different job,
          often a different provider. Detail rows still open under /tow-requests,
          which serves both. */}
      <Route
        path="/roadside-assistance"
        element={
          <RequireAuth>
            <AppShell>
              <Outlet />
            </AppShell>
          </RequireAuth>
        }
      >
        <Route index element={<RoadsideAssistanceList />} />
      </Route>

      {/* Customers catalog — nested so future detail children plug in as siblings */}
      <Route
        path="/catalogs/customer"
        element={
          <RequireAuth>
            <AppShell>
              <Outlet />
            </AppShell>
          </RequireAuth>
        }
      >
        <Route index element={<CustomersList />} />
        <Route path=":id" element={<CustomerDetail />} />
      </Route>

      {/* Tow Providers catalog — nested so future detail children plug in as siblings */}
      <Route
        path="/catalogs/towing-providers"
        element={
          <RequireAuth>
            <AppShell>
              <Outlet />
            </AppShell>
          </RequireAuth>
        }
      >
        <Route index element={<TowProvidersList />} />
        <Route path=":id" element={<TowProviderDetail />} />
      </Route>

      {/* Drivers catalog — nested so future detail children plug in as siblings */}
      <Route
        path="/catalogs/drivers"
        element={
          <RequireAuth>
            <AppShell>
              <Outlet />
            </AppShell>
          </RequireAuth>
        }
      >
        <Route index element={<DriversList />} />
        <Route path=":id" element={<DriverDetail />} />
      </Route>

      {/* Units catalog — nested so future detail children plug in as siblings */}
      <Route
        path="/catalogs/units"
        element={
          <RequireAuth>
            <AppShell>
              <Outlet />
            </AppShell>
          </RequireAuth>
        }
      >
        <Route index element={<UnitsList />} />
        <Route path=":id" element={<UnitDetail />} />
      </Route>

      {/* Trailers catalog — the other half of a customer's fleet alongside Units.
          Route segment matches the SysSubMenu row ("trailers") so the
          controller's `catalogs/trailers` permission literal lines up. */}
      <Route
        path="/catalogs/trailers"
        element={
          <RequireAuth>
            <AppShell>
              <Outlet />
            </AppShell>
          </RequireAuth>
        }
      >
        <Route index element={<TrailersList />} />
      </Route>

      {/* Adjusters catalog — nested so future detail children plug in as siblings */}
      <Route
        path="/catalogs/adjusters"
        element={
          <RequireAuth>
            <AppShell>
              <Outlet />
            </AppShell>
          </RequireAuth>
        }
      >
        <Route index element={<AdjustersList />} />
        <Route path=":id" element={<AdjusterDetail />} />
      </Route>

      {/* Documents catalog — nested so future detail children plug in as siblings */}
      <Route
        path="/catalogs/documents"
        element={
          <RequireAuth>
            <AppShell>
              <Outlet />
            </AppShell>
          </RequireAuth>
        }
      >
        <Route index element={<DocumentsList />} />
      </Route>

      {/* Lookups catalog — nested so future detail children plug in as siblings */}
      <Route
        path="/catalogs/lookups"
        element={
          <RequireAuth>
            <AppShell>
              <Outlet />
            </AppShell>
          </RequireAuth>
        }
      >
        <Route index element={<LookupsList />} />
      </Route>

      {/* Stub catalog routes — preview-only list views for catalogs whose
          dedicated CRUD UIs aren't built yet. Each calls a real backend
          endpoint and renders the rows in a basic searchable table. */}
      <Route
        path="/catalogs/lookup-types"
        element={<RequireAuth><AppShell><Outlet /></AppShell></RequireAuth>}
      >
        <Route index element={
          <CatalogStubPage
            titleKey="SUBMENU.lookupType"
            endpoint="/api/v1/get-list-lookup-type"
            description="Lookup types group related FndLookup rows (e.g. documentTypeClaim, statusReport)."
          />
        } />
      </Route>
      <Route
        path="/catalogs/adjuster-users"
        element={<RequireAuth><AppShell><Outlet /></AppShell></RequireAuth>}
      >
        <Route index element={
          <CatalogStubPage
            titleKey="SUBMENU.adjustersUser"
            endpoint="/api/v1/AdjusterUser/get-list-adjuster-user"
            description="SysUser rows scoped to a PoAdjuster tenant."
          />
        } />
      </Route>
      <Route
        path="/catalogs/customer-users"
        element={<RequireAuth><AppShell><Outlet /></AppShell></RequireAuth>}
      >
        <Route index element={
          <CatalogStubPage
            titleKey="SUBMENU.customersUsers"
            endpoint="/api/v1/CustomerUser/get-list-customer-user"
            description="SysUser rows scoped to a CsCustomer tenant."
          />
        } />
      </Route>
      {/* Tow Truck Drivers — the same panel as /catalogs/drivers, filtered to
          operators who work for a tow provider rather than a customer fleet.
          One component so the two cannot drift; see DriversMode. */}
      <Route
        path="/catalogs/tow-truck-drivers"
        element={<RequireAuth><AppShell><Outlet /></AppShell></RequireAuth>}
      >
        <Route index element={<DriversList mode="towTruck" />} />
      </Route>
      <Route
        path="/catalogs/tow-provider-users"
        element={<RequireAuth><AppShell><Outlet /></AppShell></RequireAuth>}
      >
        <Route index element={
          <CatalogStubPage
            titleKey="SUBMENU.towProvidersUsers"
            endpoint="/api/v1/TowProviderUser/get-list-towprovider-user"
            description="SysUser rows scoped to a PoTowProvider tenant."
          />
        } />
      </Route>
      <Route
        path="/catalogs/administrators"
        element={<RequireAuth><AppShell><Outlet /></AppShell></RequireAuth>}
      >
        <Route index element={<AdministratorsCatalog />} />
      </Route>
      {/* Applications waiting for a decision. The path the notification email
          links to, so it must not move without changing OnboardingBLL too. */}
      <Route
        path="/configuration/applications"
        element={<RequireAuth><AppShell><Outlet /></AppShell></RequireAuth>}
      >
        <Route index element={<ApplicationsPage />} />
      </Route>
      {/* Who is actually opening the app. Administrator-only on the server. */}
      <Route
        path="/configuration/activity"
        element={<RequireAuth><AppShell><Outlet /></AppShell></RequireAuth>}
      >
        <Route index element={<ActivityPage />} />
      </Route>
      {/* IOSiX telematics devices and the unit each is paired to. Administrator-only on the server. */}
      <Route
        path="/configuration/telematics"
        element={<RequireAuth><AppShell><Outlet /></AppShell></RequireAuth>}
      >
        <Route index element={<TelematicsDevicesPage />} />
      </Route>
      {/* The dispatch knobs: bands, ranking weights, rating thresholds and the
          call-centre desk. Administrator-only on the server, and the page says
          so to anybody else rather than showing a form it cannot save. */}
      <Route
        path="/configuration/dispatch-settings"
        element={<RequireAuth><AppShell><Outlet /></AppShell></RequireAuth>}
      >
        <Route index element={<DispatchSettingsPage />} />
      </Route>
      {/* Chat. The backend menu hardcodes "/catalogs/administrator-chat", so
          that path stays and renders the real page rather than the catalog
          stub it used to — the stub listed raw chat-group rows, which is what
          the dashboard's Unread Messages tile used to drop people into. */}
      <Route
        path="/catalogs/administrator-chat"
        element={<RequireAuth><AppShell><Outlet /></AppShell></RequireAuth>}
      >
        <Route index element={<ChatPage />} />
      </Route>
      <Route path="/chat" element={<Navigate to="/catalogs/administrator-chat" replace />} />

      {/* A provider's own wreckers, carriers, trailers and recovery gear —
          distinct from Units/Trailers, which describe a customer's fleet.
          Admins reach one provider's list with ?towProviderId. */}
      <Route
        path="/provider-equipment"
        element={<RequireAuth><AppShell><Outlet /></AppShell></RequireAuth>}
      >
        <Route index element={<ProviderEquipmentPage />} />
      </Route>

      {/* What the network can physically do. Administrators only. */}
      <Route
        path="/equipment-coverage"
        element={<RequireAuth><AppShell><Outlet /></AppShell></RequireAuth>}
      >
        <Route index element={<EquipmentCoveragePage />} />
      </Route>

      {/* Is the bidding loop working. Administrators only; the API refuses
          anyone else. */}
      <Route
        path="/reports"
        element={<RequireAuth><AppShell><Outlet /></AppShell></RequireAuth>}
      >
        <Route index element={<AdminReportsPage />} />
      </Route>

      {/* The network on a map — who covers where, and what they can do there.
          Administrators only; the API refuses anyone else. */}
      <Route
        path="/provider-map"
        element={<RequireAuth><AppShell><Outlet /></AppShell></RequireAuth>}
      >
        <Route index element={<ProviderMapPage />} />
      </Route>

      {/* Who a tow would be offered to, in order, and why everyone else was
          passed over. Read-only; administrators only — the API refuses anyone
          else. */}
      <Route
        path="/provider-ranking"
        element={<RequireAuth><AppShell><Outlet /></AppShell></RequireAuth>}
      >
        <Route index element={<ProviderRankingPage />} />
      </Route>

      {/* Every provider's rates side by side, with network averages.
          Administrators only — the API refuses anyone else. */}
      <Route
        path="/provider-pricing"
        element={<RequireAuth><AppShell><Outlet /></AppShell></RequireAuth>}
      >
        <Route index element={<ProviderPricingOverview />} />
      </Route>

      {/* A tow provider's company details and rate card — the onboarding form
          made permanent. Providers get their own; admins pass ?towProviderId. */}
      <Route
        path="/provider-profile"
        element={<RequireAuth><AppShell><Outlet /></AppShell></RequireAuth>}
      >
        <Route index element={<ProviderProfilePage />} />
      </Route>

      {/* Document requests across every claim the viewer can see. Scope is the
          server's decision from the token, so there is no id in the path. */}
      <Route
        path="/document-requests"
        element={<RequireAuth><AppShell><Outlet /></AppShell></RequireAuth>}
      >
        <Route index element={<DocumentRequestsList />} />
      </Route>

      {/* Recycle Bin — read-only list of soft-deleted claim files */}
      <Route
        path="/recycle-bin"
        element={
          <RequireAuth>
            <AppShell>
              <Outlet />
            </AppShell>
          </RequireAuth>
        }
      >
        <Route index element={<RecycleBinList />} />
      </Route>

      {/* Configuration — Roles list */}
      <Route
        path="/configuration/roles"
        element={
          <RequireAuth>
            <AppShell>
              <Outlet />
            </AppShell>
          </RequireAuth>
        }
      >
        <Route index element={<RolesList />} />
        {/* Permission matrix for one role (mockup 04, screen 3) */}
        <Route path=":roleId/permissions" element={<RolePermissionMatrix />} />
      </Route>

      {/* Configuration — Users list */}
      <Route
        path="/configuration/users"
        element={
          <RequireAuth>
            <AppShell>
              <Outlet />
            </AppShell>
          </RequireAuth>
        }
      >
        <Route index element={<UsersList />} />
      </Route>

      {/* Configuration — Terms & Conditions list */}
      <Route
        path="/configuration/terms-conditions"
        element={
          <RequireAuth>
            <AppShell>
              <Outlet />
            </AppShell>
          </RequireAuth>
        }
      >
        <Route index element={<TermsConditionsList />} />
      </Route>

      {/* Routings — read-only list + detail of pre-planned trip assignments */}
      <Route
        path="/routings"
        element={
          <RequireAuth>
            <AppShell>
              <Outlet />
            </AppShell>
          </RequireAuth>
        }
      >
        <Route index element={<RoutingsList />} />
        <Route path=":id" element={<RoutingDetail />} />
      </Route>

      {/* Claims — list (slice 137) + detail editor (slice 104) */}
      <Route
        path="/claims"
        element={
          <RequireAuth>
            <AppShell>
              <Outlet />
            </AppShell>
          </RequireAuth>
        }
      >
        <Route index element={<ClaimsList />} />
        <Route path=":id" element={<ClaimDetail />} />
      </Route>

      {/* Preferences — user preferences page (slice 320) */}
      <Route
        path="/preferences"
        element={
          <RequireAuth>
            <AppShell>
              <PreferencesPage />
            </AppShell>
          </RequireAuth>
        }
      />

      {/* Remaining stub routes — each will be replaced by a lazy feature component */}
      {[
        '/catalogs',
        '/configuration',
        '/chat',
        '/recycle-bin',
        '/rewards',
        '/rrhh',
      ].map((path) => (
        <Route
          key={path}
          path={`${path}/*`}
          element={
            <AppShell>
              <HomePlaceholder />
            </AppShell>
          }
        />
      ))}

      {/* Onboarding — behind auth but deliberately NOT behind the onboarding
          gate (see core/onboarding/route.ts), because it is where the gate
          sends people. Outside AppShell: a tenant who cannot use the app yet
          should not be shown a sidebar full of places they cannot go. */}
      <Route
        path="/onboarding"
        element={
          <RequireAuth>
            <OnboardingPage />
          </RequireAuth>
        }
      />

      {/* A customer's own profile — the customer half of what the wizard links
          to, and a normal portal page afterwards. */}
      <Route
        path="/customer-profile"
        element={
          <RequireAuth>
            <AppShell>
              <CustomerProfilePage />
            </AppShell>
          </RequireAuth>
        }
      />

      {/* What is wrong, and what is supposed to happen. Behind RequireAuth
          and inside AppShell, deliberately unlike the public /updates: these
          two describe our own gaps, which is not something to hand a tow
          company or a customer. Each page also checks for an administrator
          itself, because the route table has no role vocabulary. */}
      <Route
        path="/issues"
        element={
          <RequireAuth>
            <AppShell>
              <IssuesPage />
            </AppShell>
          </RequireAuth>
        }
      />
      <Route
        path="/workflows"
        element={
          <RequireAuth>
            <AppShell>
              <WorkflowsPage />
            </AppShell>
          </RequireAuth>
        }
      />

      {/* Login — outside AppShell and RequireAuth to avoid infinite redirect loop */}
      <Route path="/login" element={<Login />} />

      {/* Self-serve registration — public, for the same reason as /login: it is
          how an account comes to exist, so it cannot require one. */}
      <Route path="/signup" element={<SignupPage />} />

      {/* Product updates — public, outside auth and outside the site gate.
          Sent to people who have no account here, which is the whole point:
          the page measures whether they actually read it. */}
      <Route path="/updates" element={<UpdatesPage />} />
      {/* Declared before the dynamic segment for readability; React Router
          ranks the static path higher regardless, so "archive" is never
          mistaken for an edition date. */}
      <Route path="/updates/archive" element={<UpdatesArchive />} />
      <Route path="/updates/:date" element={<UpdatesPage />} />

      {/* Forgot-password — same public-shell pattern as /login */}
      <Route path="/forgot-password" element={<ForgotPassword />} />

      {/* QA / Testing Console — internal, flag-gated, and deliberately outside
          both RequireAuth and AppShell: a tester has to be able to record that
          the login page is broken. When the flag is off the route is never
          registered, so /testing falls through to the 404 below. */}
      {TESTING_CONSOLE_ENABLED && (
        <Route path="/testing" element={<TestingConsolePage />} />
      )}

      {/* Catch-all */}
      <Route path="/404" element={<NotFound />} />
      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
    </Suspense>
    </ErrorBoundary>
  );
}
